import { DEFAULT_RACE_CONFIG } from './race-state';
import type {
  ReadinessRaceState,
  SanityReadinessRaceConfig
} from '@lib/sanity/types';

export type RaceBarPositions = {
  /** Title pace flag, fixed near the right edge of the track */
  pacePercent: number;
  /** Amber user fill */
  userPercent: number;
  /** Dashed GW6 ghost marker; the hatched zone spans ghost → pace */
  ghostPercent: number;
};

type RaceBarInput = {
  state: ReadinessRaceState;
  gap: number;
  projectedGap: number | null;
};

const clamp = (value: number, min: number, max: number): number =>
  Math.max(min, Math.min(max, value));

/**
 * Maps real points values to track percentages (spec 6.3 race-bar
 * mapping). Title pace is fixed; user and ghost scale linearly from
 * points-behind, clamped so the ordering ghost < user < pace always
 * holds. The ahead state pins the user near the pace flag with a
 * pack-side floor for the ghost; lateStart uses fixed positions because
 * the manual path has no real standings.
 */
export const mapRaceBarPositions = (
  { state, gap, projectedGap }: RaceBarInput,
  config: SanityReadinessRaceConfig = DEFAULT_RACE_CONFIG
): RaceBarPositions => {
  const pacePercent = config.titlePacePercent;
  const userCeiling = pacePercent - config.minSeparationPercent;

  if (state === 'lateStart') {
    return {
      pacePercent,
      userPercent: config.userMinPercent,
      ghostPercent: clamp(
        config.ghostFloorPercent,
        0,
        config.userMinPercent - config.minSeparationPercent
      )
    };
  }

  if (state === 'ahead') {
    const userPercent = Math.min(config.userMaxPercent, userCeiling);
    return {
      pacePercent,
      userPercent,
      ghostPercent: clamp(
        userPercent - config.aheadGhostDropPercent,
        config.ghostFloorPercent,
        userPercent - config.minSeparationPercent
      )
    };
  }

  const userPercent = clamp(
    userCeiling - gap * config.gapToPercentScale,
    config.userMinPercent,
    userCeiling
  );
  const ghostPercent = clamp(
    pacePercent - (projectedGap ?? gap) * config.gapToPercentScale,
    config.ghostFloorPercent,
    userPercent - config.minSeparationPercent
  );

  return { pacePercent, userPercent, ghostPercent };
};
